/* ============================================================
   234LABS — hero.js
   bolapsd-style hero: cross-fade slideshow + hamburger dropdown.
   ============================================================ */
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.hero__slide')];
    this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.index < 0) this.index = 0;
    this.timer = null;
    this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 2000);
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.initMenu();
    this.syncVideos();

    if (this.slides.length > 1) {
      // pause only when the tab is hidden — not on hover
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else {
          this.syncVideos();
          this.start();
        }
      });
      if (this.autoplay && !this.reduce) this.start();
    }
  }

  /* ---- slideshow ---- */
  go(i) {
    this.index = (i + this.slides.length) % this.slides.length;
    this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
    this.syncVideos(true);
    if (this.timer) this.schedule();
  }
  next() {
    this.go(this.index + 1);
  }
  // only the visible slide's video plays; it restarts each time its slide comes round
  syncVideos(restart = false) {
    this.slides.forEach((s, n) => {
      const v = s.querySelector('video');
      if (!v) return;
      if (n === this.index && !this.reduce) {
        if (restart) v.currentTime = 0;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        v.pause();
      }
    });
  }
  // time on the current slide: normal interval, or the whole video if asked
  schedule() {
    clearTimeout(this.timer);
    const slide = this.slides[this.index];
    const v = slide.querySelector('video');
    let wait = this.interval;
    if (v && slide.dataset.holdVideo === 'true') {
      if (isFinite(v.duration) && v.duration > 0) {
        wait = Math.max(wait, v.duration * 1000);
      } else {
        // length not known yet — re-schedule once it is
        v.addEventListener('loadedmetadata', () => this.timer && this.schedule(), { once: true });
      }
    }
    this.timer = setTimeout(() => this.next(), wait);
  }
  start() {
    if (!this.autoplay || this.timer) return;
    this.schedule();
  }
  stop() {
    clearTimeout(this.timer);
    this.timer = null;
    this.slides.forEach((s) => s.querySelector('video')?.pause());
  }
  disconnectedCallback() {
    this.stop();
  }

  /* ---- hamburger dropdown ---- */
  initMenu() {
    const nav = this.querySelector('[data-hero-nav]');
    const burger = this.querySelector('[data-hero-burger]');
    const menu = this.querySelector('[data-hero-menu]');
    if (!nav || !burger || !menu) return;

    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', String(open));
      menu.hidden = !open;
      if (!open) {
        menu.querySelectorAll('[data-hero-submenu]').forEach((b) => {
          b.setAttribute('aria-expanded', 'false');
          b.nextElementSibling.hidden = true;
        });
      }
    };

    burger.addEventListener('click', (e) => {
      e.stopPropagation();
      setOpen(menu.hidden);
    });

    menu.addEventListener('click', (e) => {
      const sub = e.target.closest('[data-hero-submenu]');
      if (!sub) return;
      e.preventDefault();
      const open = sub.getAttribute('aria-expanded') === 'true';
      sub.setAttribute('aria-expanded', String(!open));
      sub.nextElementSibling.hidden = open;
    });

    document.addEventListener('click', (e) => {
      if (!nav.contains(e.target) && !menu.hidden) setOpen(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !menu.hidden) {
        setOpen(false);
        burger.focus();
      }
    });
  }
}

if (!customElements.get('hero-slideshow')) customElements.define('hero-slideshow', HeroSlideshow);
